import {gql} from '@apollo/client';
import {getTestProps} from '@benchling/js-test-utils';
import {Button, createStyles, Group, Menu, SegmentedControl} from '@mantine/core';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import _ from 'underscore';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import {useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import HomeDashboardActions from 'home-dashboard/HomeDashboardActions';
import HomeDashboardStore from 'home-dashboard/HomeDashboardStore';
import useOnMount from 'ui-library/hooks/useOnMount';
import useStoreSelector from 'ui-library/hooks/useStoreSelector';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import {HomepageAssignedProcessTasksQuery} from './__generated/HomepageAssignedProcessTasksQuery';
import CardSection from './CardSection';
import {HOMEPAGE_SECTION_TEST_COMPONENT} from './HomepageConstants';
import {
  getEnabledMyWorkCategories,
  hasReviewsLicense,
  shouldShowAssignedProcessTasks,
} from './HomepageVisibility';
import EntriesRows from './my-work/EntriesRows';
import {
  MyWorkCategory,
  REVIEW_ITEM_STATUS_LABELS,
  REVIEW_STATUS_LABEL_TO_SUMMARY_KEY,
  TASK_STATUS_LABELS,
  TASK_STATUS_LABEL_TO_SUMMARY_KEY,
} from './my-work/MyWorkConstants';
import {MyWorkStatusMenuItem, StatusMenuCountKey} from './my-work/MyWorkStatusMenuItem';
import {
  getCategoryCounts,
  getSelectedReviewSummaryIds,
  getSelectedTaskIds,
  makeTasksReviewSummary,
} from './my-work/MyWorkUtils';
import {openMyWorkReviewableSearch, openAllAssignedTasksSearch} from './my-work/openMyWorkSearch';
import TasksRows from './my-work/TasksRows';
import WorksheetsRows from './my-work/WorksheetsRows';

export const HOMEPAGE_ASSIGNED_PROCESS_TASKS_QUERY = gql`
  query HomepageAssignedProcessTasksQuery @skipAudit @engComponent(name: NAVIGATION) {
    assignedProcessTaskStatusSummaries(
      processTaskStatusTypes: [PENDING, IN_PROGRESS, COMPLETED, FAILED, INVALID, SKIPPED, CANCELLED]
    ) {
      ... on ProcessTaskStatusSummaryItemNode {
        count
        taskIds
        status {
          ... on ProcessTaskStatusNode {
            id
            displayName
            statusType
          }
        }
      }
    }
  }
`;

const TEST_COMPONENT = HOMEPAGE_SECTION_TEST_COMPONENT.MY_WORK;

const useStyles = createStyles((theme) => ({
  filterRow: {
    borderBottom: `1px solid ${theme.colors.gray[2]}`,
    padding: theme.spacing.md,
  },
}));

/** Entry, worksheet, and process-task work queues with per-category status filters. */
export default function MyWorkSection(): JSX.Element | null {
  const showAssignedProcessTasks = shouldShowAssignedProcessTasks();

  useOnMount(() => {
    // Browsers cap the number of simultaneous network requests to a given domain - typically around 6.
    // <Sidepanel> (which renders this component) does a few expensive GraphQL queries, and since Apollo
    // always defers queries for a tick, if we don't match that behavior here, these network requests are
    // fired first, and ultimately make page load slower (we want the slow network requests to start as soon
    // as possible).
    _.defer(() => {
      if (hasReviewsLicense()) {
        HomeDashboardActions.fetchEntryReviewSummary();
        HomeDashboardActions.fetchWorksheetReviewSummary();
      }
    });
  });

  const entryReviewSummary = useStoreSelector(
    HomeDashboardStore,
    useCallback((store) => store.getEntryReviewSummary(), [])
  );
  const worksheetReviewSummary = useStoreSelector(
    HomeDashboardStore,
    useCallback((store) => store.getWorksheetReviewSummary(), [])
  );

  const enabledCategories = useMemo(() => getEnabledMyWorkCategories(), []);

  const [selectedCategory, setSelectedCategory] = useState<MyWorkCategory>(MyWorkCategory.ENTRIES);
  const [selectedEntryStatuses, setSelectedEntryStatuses] = useState(REVIEW_ITEM_STATUS_LABELS);
  const [selectedWorksheetStatuses, setSelectedWorksheetStatuses] = useState(REVIEW_ITEM_STATUS_LABELS);
  const [selectedTaskStatuses, setSelectedTaskStatuses] = useState(TASK_STATUS_LABELS);
  const {classes} = useStyles();

  useEffect(() => {
    if (enabledCategories.length > 0 && !enabledCategories.includes(selectedCategory)) {
      setSelectedCategory(enabledCategories[0]);
    }
  }, [enabledCategories, selectedCategory]);

  const tasksQueryResult = useSkippableBenchlingQuery<HomepageAssignedProcessTasksQuery>(
    HOMEPAGE_ASSIGNED_PROCESS_TASKS_QUERY,
    {skip: !showAssignedProcessTasks}
  );
  const tasksReviewSummary = useMemo(() => makeTasksReviewSummary(tasksQueryResult), [tasksQueryResult]);

  if (enabledCategories.length === 0) {
    return null;
  }

  const categoryUi = {
    [MyWorkCategory.ENTRIES]: {
      statusLabels: REVIEW_ITEM_STATUS_LABELS,
      selectedStatuses: selectedEntryStatuses,
      setSelectedStatuses: setSelectedEntryStatuses,
    },
    [MyWorkCategory.WORKSHEETS]: {
      statusLabels: REVIEW_ITEM_STATUS_LABELS,
      selectedStatuses: selectedWorksheetStatuses,
      setSelectedStatuses: setSelectedWorksheetStatuses,
    },
    [MyWorkCategory.TASKS]: {
      statusLabels: TASK_STATUS_LABELS,
      selectedStatuses: selectedTaskStatuses,
      setSelectedStatuses: setSelectedTaskStatuses,
    },
  }[selectedCategory];

  const {entriesCount, worksheetsCount, tasksCount} = getCategoryCounts({
    entryReviewSummary,
    worksheetReviewSummary,
    tasksQueryResult,
    showEntriesCategory: enabledCategories.includes(MyWorkCategory.ENTRIES),
    showWorksheetsCategory: enabledCategories.includes(MyWorkCategory.WORKSHEETS),
    showTasksCategory: enabledCategories.includes(MyWorkCategory.TASKS),
  });

  const statusCountsForMenu =
    selectedCategory === MyWorkCategory.ENTRIES
      ? entryReviewSummary
      : selectedCategory === MyWorkCategory.WORKSHEETS
        ? worksheetReviewSummary
        : tasksReviewSummary;

  const getSummaryKeyForStatusLabel = (statusLabel: string): StatusMenuCountKey =>
    selectedCategory === MyWorkCategory.TASKS
      ? TASK_STATUS_LABEL_TO_SUMMARY_KEY[statusLabel]
      : REVIEW_STATUS_LABEL_TO_SUMMARY_KEY[statusLabel];

  const toDoCategoryLabels: Record<MyWorkCategory, string> = {
    [MyWorkCategory.ENTRIES]: `Entries (${entriesCount})`,
    [MyWorkCategory.WORKSHEETS]: `Worksheets (${worksheetsCount})`,
    [MyWorkCategory.TASKS]: `Tasks (${tasksCount})`,
  };

  const statusMenuLabel =
    categoryUi.selectedStatuses.length === categoryUi.statusLabels.length
      ? 'All statuses'
      : categoryUi.selectedStatuses.length > 1
        ? `${categoryUi.selectedStatuses.length} statuses`
        : (categoryUi.selectedStatuses[0] ?? 'Nothing selected');

  return (
    <CardSection
      title="My work"
      rightContent={
        <Button
          size="sm"
          onClick={() => {
            if (selectedCategory === MyWorkCategory.ENTRIES) {
              openMyWorkReviewableSearch([ModelTypeConstants.ENTRY, ModelTypeConstants.STAGE_ENTRY]);
            } else if (selectedCategory === MyWorkCategory.WORKSHEETS) {
              openMyWorkReviewableSearch([ModelTypeConstants.WORKSHEET]);
            } else if (selectedCategory === MyWorkCategory.TASKS) {
              openAllAssignedTasksSearch();
            }
          }}
        >
          See all {selectedCategory}
        </Button>
      }
      dataTestAttrs={{component: TEST_COMPONENT, element: 'root'}}
    >
      <Group position="apart" spacing="xs" className={classes.filterRow}>
        <SegmentedControl
          data={enabledCategories.map((category) => ({
            value: category,
            label: toDoCategoryLabels[category],
          }))}
          variant="borderless"
          value={selectedCategory.toString()}
          onChange={(value) => {
            setSelectedCategory(value as MyWorkCategory);
          }}
          {...getTestProps({component: TEST_COMPONENT, element: 'categoryControl'})}
        />
        <Menu position="bottom-end" width="300px" withinPortal={true} closeOnItemClick={false}>
          <Menu.Target>
            <Button
              variant="ghost"
              size="md"
              rightIcon={<BenchlingIcon icon="chevron-down" />}
              {...getTestProps({component: TEST_COMPONENT, element: 'statusMenuButton'})}
            >
              {statusMenuLabel}
            </Button>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item
              key="all"
              {...getTestProps({component: TEST_COMPONENT, element: 'statusMenuItem', key: 'toggleAll'})}
              onClick={() => {
                categoryUi.setSelectedStatuses(
                  categoryUi.selectedStatuses.length === categoryUi.statusLabels.length
                    ? []
                    : categoryUi.statusLabels
                );
              }}
            >
              Toggle all
            </Menu.Item>
            <Menu.Divider />
            {categoryUi.statusLabels.map((statusLabel) => (
              <MyWorkStatusMenuItem
                key={statusLabel}
                isSelected={categoryUi.selectedStatuses.includes(statusLabel)}
                setIsSelected={(selected) => {
                  categoryUi.setSelectedStatuses(
                    selected
                      ? [...categoryUi.selectedStatuses, statusLabel]
                      : categoryUi.selectedStatuses.filter((s) => s !== statusLabel)
                  );
                }}
                statusLabel={statusLabel}
                statusCounts={statusCountsForMenu}
                summaryKey={getSummaryKeyForStatusLabel(statusLabel)}
                isTask={selectedCategory === MyWorkCategory.TASKS}
                dataTestAttrs={{
                  component: TEST_COMPONENT,
                  element: 'statusMenuItem',
                  key: getSummaryKeyForStatusLabel(statusLabel),
                }}
              />
            ))}
          </Menu.Dropdown>
        </Menu>
      </Group>
      <div>
        {selectedCategory === MyWorkCategory.ENTRIES && (
          <EntriesRows
            selectedEntryIds={getSelectedReviewSummaryIds(entryReviewSummary, selectedEntryStatuses)}
          />
        )}
        {selectedCategory === MyWorkCategory.WORKSHEETS && (
          <WorksheetsRows
            selectedWorksheetIds={getSelectedReviewSummaryIds(
              worksheetReviewSummary,
              selectedWorksheetStatuses
            )}
          />
        )}
        {selectedCategory === MyWorkCategory.TASKS && (
          <TasksRows selectedTaskIds={getSelectedTaskIds(tasksReviewSummary, selectedTaskStatuses)} />
        )}
      </div>
    </CardSection>
  );
}
